import { useCallback } from "react";
import useSWR from "swr";
import { vceFirestore } from "../../configs/FirebaseConfig";
import { FIRESTORE_PICK_ROUTE_TABLE } from "../../constants/FirestoreConstant";
import {
  buildRouteFromProducts,
  buildRouteReset,
  buildRouteSync,
  planProductLink,
  withSequences,
} from "../../functions/pickRoute";

/**
 * The pick route lives in the VCE Firestore only. There is one freezer, shipped from one
 * address by both shops (see CompanyConstants), so the walking order is one shared list -
 * not something each shop keeps its own copy of. VCE is where the CMS already authenticates
 * and where the order picking app will read its waves from.
 */
const routeCollection = () => vceFirestore.collection(FIRESTORE_PICK_ROUTE_TABLE);

const SWR_KEY = "pickRoute";

// Stable reference for the not-loaded-yet case: `entries || []` would hand out a fresh array
// every render, re-running the index memo on the orders page for nothing.
const NO_ENTRIES = [];

/** Firestore refuses a batch over 500 writes, and the route is easily longer than that. */
const BATCH_LIMIT = 450;

const commitInChunks = async (operations) => {
  for (let start = 0; start < operations.length; start += BATCH_LIMIT) {
    const batch = vceFirestore.batch();
    operations.slice(start, start + BATCH_LIMIT).forEach((apply) => apply(batch));
    await batch.commit();
  }
};

const fetchPickRoute = async () => {
  const snapshot = await routeCollection().orderBy("sequence").get();
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

/**
 * Read and maintain the pick route - the explicit order in which the freezer is walked.
 * Reads are cached through SWR like the other collections; writes go straight to Firestore
 * and then revalidate.
 */
export const usePickRoute = () => {
  const { data: entries, error, isLoading, mutate } = useSWR(SWR_KEY, fetchPickRoute, {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    dedupingInterval: 10000,
  });

  /**
   * Persist a whole reordering. Only the entries whose position actually changed are written,
   * which keeps a single drag to a handful of writes instead of the entire list.
   */
  const saveOrder = useCallback(
    async (orderedEntries) => {
      const renumbered = withSequences(orderedEntries);
      const previous = new Map((entries || []).map((entry) => [entry.id, entry.sequence]));
      const changed = renumbered.filter((entry) => !!entry.id && previous.get(entry.id) !== entry.sequence);

      if (changed.length === 0) return renumbered;

      await commitInChunks(
        changed.map((entry) => (batch) =>
          batch.update(routeCollection().doc(entry.id), { sequence: entry.sequence, updatedAt: Date.now() }),
        ),
      );
      await mutate();
      return renumbered;
    },
    [entries, mutate],
  );

  /**
   * First fill: build the route from the products themselves, VCE order first.
   * Refuses to run when there is already a route, so it can never quietly wipe manual work -
   * use syncFromProducts to top up, or resetOrder to renumber on purpose.
   */
  const generateFromProducts = useCallback(
    async (products) => {
      if (!!entries && entries.length > 0) {
        throw new Error("pickRoute/not-empty");
      }
      const seeded = buildRouteFromProducts(products);
      await commitInChunks(
        seeded.map((entry) => (batch) => batch.set(routeCollection().doc(), { ...entry, updatedAt: Date.now() })),
      );
      await mutate();
      return seeded;
    },
    [entries, mutate],
  );

  /**
   * Top up with products that have appeared since. Never moves what is already placed.
   * Returns the change set so the page can report what happened.
   */
  const syncFromProducts = useCallback(
    async (products) => {
      const changes = buildRouteSync(entries || [], products);
      const operations = [
        ...changes.created.map((entry) => (batch) =>
          batch.set(routeCollection().doc(), { ...entry, updatedAt: Date.now() }),
        ),
        ...changes.attached.map(({ entry, products: added }) => (batch) =>
          batch.update(routeCollection().doc(entry.id), {
            products: [...(entry.products || []), ...added],
            updatedAt: Date.now(),
          }),
        ),
      ];

      if (operations.length > 0) {
        await commitInChunks(operations);
        await mutate();
      }
      return changes;
    },
    [entries, mutate],
  );

  /** Renumber everything on VCE popularity. Destructive: the page confirms before calling this. */
  const resetOrder = useCallback(
    async (products) => {
      const reset = buildRouteReset(entries || [], products);
      await commitInChunks(
        reset
          .filter((entry) => !!entry.id)
          .map((entry) => (batch) =>
            batch.update(routeCollection().doc(entry.id), { sequence: entry.sequence, updatedAt: Date.now() }),
          ),
      );
      await mutate();
      return reset;
    },
    [entries, mutate],
  );

  const updateEntry = useCallback(
    async (entryId, updates) => {
      await routeCollection().doc(entryId).update({ ...updates, updatedAt: Date.now() });
      await mutate();
    },
    [mutate],
  );

  const deleteEntry = useCallback(
    async (entryId) => {
      await routeCollection().doc(entryId).delete();
      await mutate();
    },
    [mutate],
  );

  /**
   * Put a shop product on an entry - the manual fix when the two shops name the same product
   * differently and the route ended up with it on its own spot.
   *
   * This moves rather than copies, and does both sides in one batch: a half-applied move would
   * leave the product on two spots (packing slip picks whichever wins) or on none at all.
   * Returns what happened, so the page can say whether the old spot was cleared away.
   */
  const linkProductToEntry = useCallback(
    async (targetEntry, product) => {
      const plan = planProductLink(entries, targetEntry, product);
      if (!plan) return null;

      const batch = vceFirestore.batch();
      batch.update(routeCollection().doc(plan.target.id), {
        products: plan.targetProducts,
        updatedAt: Date.now(),
      });
      if (!!plan.source) {
        if (plan.removeSource) {
          batch.delete(routeCollection().doc(plan.source.id));
        } else {
          batch.update(routeCollection().doc(plan.source.id), {
            products: plan.sourceProducts,
            updatedAt: Date.now(),
          });
        }
      }
      await batch.commit();
      await mutate();
      return plan;
    },
    [entries, mutate],
  );

  const detachProduct = useCallback(
    async (entry, product) => {
      await routeCollection()
        .doc(entry.id)
        .update({
          products: (entry.products || []).filter(
            (existing) => !(existing.productId === product.productId && existing.shopSource === product.shopSource),
          ),
          updatedAt: Date.now(),
        });
      await mutate();
    },
    [mutate],
  );

  return {
    entries: entries || NO_ENTRIES,
    isLoading,
    error,
    refresh: mutate,
    saveOrder,
    generateFromProducts,
    syncFromProducts,
    resetOrder,
    updateEntry,
    deleteEntry,
    linkProductToEntry,
    detachProduct,
  };
};
